// src/components/Security/SecurityDashboard.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RadialBarChart, RadialBar, PieChart, Pie, Cell,
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Shield, ShieldAlert, ShieldCheck, ShieldOff,
  Fingerprint, Database, Globe, FileCheck, Activity,
  Lock, Layers, Key, AlertTriangle, CheckCircle,
  XCircle, Clock, TrendingUp, TrendingDown, Minus,
  ChevronRight, ExternalLink, RefreshCw, Info, Zap, Eye,
  Server, Network, Users, FileText, BarChart2, HardDrive, Bell,
} from 'lucide-react';

// ─── Source colours (used in Threat Feed) ────────────────────────────────────
const SRC_C = { GCP: '#4285f4', AZURE: '#0078d4', MONGODB: '#4ade80', DATABASE: '#a78bfa' };

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  iam:     '#8b5cf6',
  data:    '#3b82f6',
  network: '#06b6d4',
  comply:  '#f59e0b',
  ir:      '#22c55e',
  zt:      '#e879f9',
  aks:     '#0ea5e9',
  infra:   '#64748b',
  crit:    '#ef4444',
  warn:    '#f59e0b',
  ok:      '#22c55e',
  info:    '#3b82f6',
};

// ─── Static demo data ─────────────────────────────────────────────────────────

const OVERALL_SCORE = 74;

const SCORE_TREND = [68, 71, 69, 73, 70, 74, 74].map((v, i) => ({ d: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i], score: v }));

const FRAMEWORKS = [
  { id: 'pci',  name: 'PCI DSS 3.2.1',  version: 'v3.2.1',  score: 87, passed: 189, failed: 28, na: 5,  critical: 5, color: '#ef4444', nextAudit: 'Sep 2026', status: 'In Progress' },
  { id: 'soc2', name: 'SOC 2 Type II',  version: 'CC 2017',  score: 92, passed:  94, failed:  8, na: 2,  critical: 2, color: '#f59e0b', nextAudit: 'Jul 2026', status: 'Ready' },
  { id: 'gdpr', name: 'GDPR',           version: 'Art. 5',   score: 78, passed: 106, failed: 30, na: 8,  critical: 6, color: '#8b5cf6', nextAudit: 'Ongoing',  status: 'Needs Work' },
  { id: 'cis',  name: 'CIS AKS 1.4',   version: 'v1.4.0',   score: 71, passed: 122, failed: 50, na: 12, critical: 9, color: '#0ea5e9', nextAudit: 'Aug 2026', status: 'In Progress' },
];

// 7-day score trend per framework
const mkTrend = base => Array.from({ length: 7 }, (_, i) => ({ d: i, v: Math.max(0, Math.min(100, base + Math.round((Math.random() - 0.5) * 8))) }));

const CATEGORY_DATA = [
  {
    id: 'iam', name: 'Identity & Access', icon: Fingerprint, color: C.iam,
    score: 68, risk: 'HIGH',
    trend: [62, 65, 63, 68, 66, 70, 68].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'MFA Coverage',       value: '94%',  status: 'ok'   },
      { label: 'Privileged Accounts',value: '14',   status: 'warn' },
      { label: 'Keys >60 Days',      value: '3',    status: 'crit' },
      { label: 'Service Accounts',   value: '38',   status: 'info' },
    ],
    controls: { passed: 12, failed: 3, total: 15 },
    findings: [
      { sev: 'CRITICAL', msg: 'deploy-sa granted cluster-admin without change ticket' },
      { sev: 'CRITICAL', msg: 'OAuth token committed to public GitHub repository' },
      { sev: 'WARNING',  msg: '3 service account keys older than 90 days' },
      { sev: 'CRITICAL', msg: 'MFA fatigue attack on prod-admin account (7 pushes/4 min)' },
    ],
    description: 'Monitors authentication, authorization, service account lifecycle, and privileged access controls.',
  },
  {
    id: 'data', name: 'Data Protection', icon: Database, color: C.data,
    score: 72, risk: 'HIGH',
    trend: [68, 70, 72, 71, 73, 72, 72].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'Encrypted at Rest',  value: '94%',  status: 'warn' },
      { label: 'Encrypted Transit',  value: '100%', status: 'ok'   },
      { label: 'PII Datasets',       value: '7',    status: 'warn' },
      { label: 'Backup Success',     value: '96%',  status: 'warn' },
    ],
    controls: { passed: 18, failed: 4, total: 22 },
    findings: [
      { sev: 'CRITICAL', msg: 'SSNs in plaintext in Cloud Logging (142 entries)' },
      { sev: 'WARNING',  msg: 'Encryption key expires in 7 days — Azure Key Vault' },
      { sev: 'WARNING',  msg: 'MongoDB Atlas backup stored without encryption at rest' },
      { sev: 'WARNING',  msg: 'pg_dump backup missing for 3 consecutive days' },
    ],
    description: 'Tracks encryption at rest/in-transit, PII/PAN classification, backup integrity, and data lifecycle.',
  },
  {
    id: 'network', name: 'Network Security', icon: Globe, color: C.network,
    score: 63, risk: 'CRITICAL',
    trend: [70, 68, 65, 63, 62, 64, 63].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'Open CRITICAL Rules', value: '3',    status: 'crit' },
      { label: 'DDoS Protection',     value: 'ACTIVE',status: 'ok'  },
      { label: 'WAF Rules Active',    value: '142',  status: 'ok'   },
      { label: 'TLS 1.2+ Enforced',   value: '87%',  status: 'warn' },
    ],
    controls: { passed: 21, failed: 6, total: 27 },
    findings: [
      { sev: 'CRITICAL', msg: 'Port 22 opened to 0.0.0.0/0 on 12 production VMs' },
      { sev: 'CRITICAL', msg: 'DDoS attack 2.4 Gbps — mitigation active' },
      { sev: 'WARNING',  msg: 'TLS 1.0 accepted on Azure App Gateway (PCI DSS violation)' },
      { sev: 'WARNING',  msg: 'Port scan from Tor exit node 185.220.101.47' },
    ],
    description: 'Covers firewall rules, DDoS protection, WAF policies, network segmentation, and protocol security.',
  },
  {
    id: 'comply', name: 'Compliance', icon: FileCheck, color: C.comply,
    score: 81, risk: 'MEDIUM',
    trend: [78, 80, 79, 81, 80, 82, 81].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'PCI DSS Score',   value: '87%', status: 'warn' },
      { label: 'SOC 2 Score',     value: '92%', status: 'ok'   },
      { label: 'GDPR Score',      value: '78%', status: 'warn' },
      { label: 'CIS AKS Score',   value: '71%', status: 'warn' },
    ],
    controls: { passed: 389, failed: 66, total: 455 },
    findings: [
      { sev: 'CRITICAL', msg: 'PCI DSS: cardholder PAN found outside CDE boundary' },
      { sev: 'WARNING',  msg: 'SOC 2 CC6.1: access review overdue for 14 privileged accounts' },
      { sev: 'WARNING',  msg: 'GDPR: 28k user records retained beyond 3-year limit' },
    ],
    description: 'Enforces PCI DSS 3.2.1, SOC 2 Type II, GDPR, and internal policy controls with automated evidence collection.',
  },
  {
    id: 'ir', name: 'Monitoring & IR', icon: Activity, color: C.ir,
    score: 88, risk: 'LOW',
    trend: [82, 85, 86, 87, 88, 88, 88].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'MTTD',          value: '4.2 min', status: 'ok'   },
      { label: 'MTTR',          value: '18.7 min',status: 'ok'   },
      { label: 'Open Incidents',value: '3',       status: 'warn' },
      { label: 'SOC Coverage',  value: '24/7',    status: 'ok'   },
    ],
    controls: { passed: 28, failed: 4, total: 32 },
    findings: [
      { sev: 'CRITICAL', msg: 'Sentinel: brute-force → priv-esc kill chain correlated' },
      { sev: 'WARNING',  msg: '2,400 low-fidelity alerts suppressed — review suppression rules' },
      { sev: 'INFO',     msg: 'Log retention policy updated to 365 days (SOC 2 compliant)' },
    ],
    description: 'SIEM integration, incident detection (MTTD/MTTR), playbook automation, and threat hunting.',
  },
  {
    id: 'zt', name: 'Zero Trust', icon: Lock, color: C.zt,
    score: 61, risk: 'HIGH',
    trend: [55, 58, 56, 60, 59, 62, 61].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'Device Compliance', value: '89%', status: 'warn' },
      { label: 'mTLS Coverage',     value: '94%', status: 'ok'   },
      { label: 'Segmented Workloads',value: '76%',status: 'warn' },
      { label: 'Identity-First Access',value:'68%',status: 'crit'},
    ],
    controls: { passed: 19, failed: 8, total: 27 },
    findings: [
      { sev: 'CRITICAL', msg: 'api-service accessing 12 secrets outside normal baseline' },
      { sev: 'WARNING',  msg: 'Unmanaged device accessing prod Azure resources' },
      { sev: 'WARNING',  msg: 'Istio: 3 services sending plaintext east-west traffic' },
    ],
    description: 'Never-trust-always-verify posture: device identity, microsegmentation, mTLS, and continuous validation.',
  },
  {
    id: 'aks', name: 'AKS Security', icon: Layers, color: C.aks,
    score: 58, risk: 'CRITICAL',
    trend: [64, 62, 60, 58, 56, 58, 58].map((v, i) => ({ d: i, v })),
    metrics: [
      { label: 'CIS Benchmark',      value: '71%', status: 'warn' },
      { label: 'Privileged Containers',value: '1', status: 'crit' },
      { label: 'Critical CVEs',      value: '3',   status: 'crit' },
      { label: 'RBAC Misconfigs',    value: '2',   status: 'crit' },
    ],
    controls: { passed: 31, failed: 13, total: 44 },
    findings: [
      { sev: 'CRITICAL', msg: 'Privileged container running in prod (4h 22m, no limits)' },
      { sev: 'CRITICAL', msg: 'payments-service:2.1.4 has CVE-2024-3094 (CVSS 10.0)' },
      { sev: 'WARNING',  msg: 'Kubernetes 1.26.x EOL — 14 unpatched CVEs' },
      { sev: 'WARNING',  msg: 'etcd secrets not encrypted at rest (847 K8s Secrets)' },
    ],
    description: 'Kubernetes security posture: CIS Benchmark, image vulnerabilities, RBAC, Pod Security, and etcd encryption.',
  },
];

// PCI DSS & SOC 2 control detail
const PCI_CONTROLS = [
  { req: '1.1',  title: 'Establish firewall configuration standards',          status: 'PASS',   cat: 'Network Security',   critical: false },
  { req: '1.2',  title: 'Restrict inbound/outbound traffic to CDE',            status: 'FAIL',   cat: 'Network Security',   critical: true  },
  { req: '1.3',  title: 'Prohibit direct public access to CDE',                status: 'PASS',   cat: 'Network Security',   critical: false },
  { req: '2.1',  title: 'Do not use vendor-supplied defaults',                  status: 'PASS',   cat: 'Infrastructure',     critical: false },
  { req: '2.2',  title: 'System configuration standards',                       status: 'WARN',   cat: 'Infrastructure',     critical: false },
  { req: '3.1',  title: 'Keep cardholder data storage to minimum',             status: 'FAIL',   cat: 'Data Protection',    critical: true  },
  { req: '3.2',  title: 'Do not store sensitive authentication data',           status: 'PASS',   cat: 'Data Protection',    critical: false },
  { req: '3.4',  title: 'Render PAN unreadable anywhere stored',               status: 'FAIL',   cat: 'Data Protection',    critical: true  },
  { req: '4.1',  title: 'Encrypt transmission of CHD across open networks',    status: 'PASS',   cat: 'Network Security',   critical: false },
  { req: '4.2',  title: 'Prohibit unprotected PANs by end-user messaging',     status: 'PASS',   cat: 'Data Protection',    critical: false },
  { req: '6.1',  title: 'Security vulnerability identification process',        status: 'PASS',   cat: 'AKS Security',       critical: false },
  { req: '6.2',  title: 'Protect against known vulnerabilities',               status: 'FAIL',   cat: 'AKS Security',       critical: true  },
  { req: '6.3',  title: 'Secure development and review processes',             status: 'WARN',   cat: 'AKS Security',       critical: false },
  { req: '7.1',  title: 'Limit access to system components and CHD',           status: 'PASS',   cat: 'Identity & Access',  critical: false },
  { req: '7.2',  title: 'Establish access control systems',                    status: 'WARN',   cat: 'Identity & Access',  critical: false },
  { req: '8.1',  title: 'Manage user IDs and authentication',                  status: 'PASS',   cat: 'Identity & Access',  critical: false },
  { req: '8.2',  title: 'Proper identification and authentication',            status: 'PASS',   cat: 'Identity & Access',  critical: false },
  { req: '8.3',  title: 'Secure individual non-consumer authentication',       status: 'FAIL',   cat: 'Identity & Access',  critical: true  },
  { req: '10.1', title: 'Link all access to system components to each user',   status: 'PASS',   cat: 'Monitoring & IR',    critical: false },
  { req: '10.2', title: 'Implement audit trails for system access',            status: 'PASS',   cat: 'Monitoring & IR',    critical: false },
  { req: '10.5', title: 'Secure audit trails from destruction/modification',   status: 'WARN',   cat: 'Monitoring & IR',    critical: false },
  { req: '11.2', title: 'Run internal/external network vulnerability scans',   status: 'PASS',   cat: 'Network Security',   critical: false },
  { req: '11.4', title: 'Detect/prevent intrusions into network',              status: 'WARN',   cat: 'Network Security',   critical: false },
  { req: '12.1', title: 'Establish and maintain an information security policy',status:'PASS',   cat: 'Compliance',         critical: false },
  { req: '12.10',title: 'Implement an incident response plan',                 status: 'PASS',   cat: 'Monitoring & IR',    critical: false },
];

const SOC2_CONTROLS = [
  { id: 'CC1.1', title: 'COSO Principle 1: Integrity & ethical values',        status: 'PASS', criteria: 'Common Criteria', cat: 'Compliance'        },
  { id: 'CC2.1', title: 'COSO Principle 13: External parties communication',   status: 'PASS', criteria: 'Common Criteria', cat: 'Compliance'        },
  { id: 'CC3.1', title: 'Specify security objectives for organization',         status: 'WARN', criteria: 'Common Criteria', cat: 'Compliance'        },
  { id: 'CC5.1', title: 'COSO Principle 10: Select and develop controls',      status: 'PASS', criteria: 'Common Criteria', cat: 'Compliance'        },
  { id: 'CC6.1', title: 'Logical and physical access controls implemented',    status: 'FAIL', criteria: 'Logical Access',  cat: 'Identity & Access' },
  { id: 'CC6.2', title: 'New internal/external users registered',              status: 'PASS', criteria: 'Logical Access',  cat: 'Identity & Access' },
  { id: 'CC6.3', title: 'Internal/external users removed when no longer needed',status:'WARN', criteria: 'Logical Access',  cat: 'Identity & Access' },
  { id: 'CC6.6', title: 'Logical access restricted from outside boundaries',   status: 'PASS', criteria: 'Logical Access',  cat: 'Network Security'  },
  { id: 'CC6.7', title: 'Transmission/receipt/movement of information protected',status:'PASS',criteria: 'Logical Access',  cat: 'Data Protection'   },
  { id: 'CC6.8', title: 'Controls prevent or detect unauthorized software',    status: 'WARN', criteria: 'Logical Access',  cat: 'AKS Security'      },
  { id: 'CC7.1', title: 'Infrastructure components monitored for anomalies',   status: 'PASS', criteria: 'System Ops',      cat: 'Monitoring & IR'   },
  { id: 'CC7.2', title: 'Security events evaluated and response established',  status: 'PASS', criteria: 'System Ops',      cat: 'Monitoring & IR'   },
  { id: 'CC7.3', title: 'Event responses evaluated to meet objectives',        status: 'PASS', criteria: 'System Ops',      cat: 'Monitoring & IR'   },
  { id: 'CC8.1', title: 'Change management process in place',                  status: 'WARN', criteria: 'Change Mgmt',     cat: 'Compliance'        },
  { id: 'CC9.1', title: 'Risk mitigation activities assessed and evaluated',   status: 'PASS', criteria: 'Risk Mgmt',       cat: 'Compliance'        },
  { id: 'A1.2',  title: 'Redundancy/DR for availability requirements',         status: 'PASS', criteria: 'Availability',    cat: 'Infrastructure'    },
  { id: 'C1.1',  title: 'Confidential information identified and handled',     status: 'FAIL', criteria: 'Confidentiality', cat: 'Data Protection'   },
  { id: 'C1.2',  title: 'Confidential information disposed of properly',       status: 'WARN', criteria: 'Confidentiality', cat: 'Data Protection'   },
  { id: 'P3.1',  title: 'Personal info collected consistent with objectives',  status: 'FAIL', criteria: 'Privacy',         cat: 'Compliance'        },
  { id: 'P5.1',  title: 'Retention of personal info meets policy',             status: 'FAIL', criteria: 'Privacy',         cat: 'Compliance'        },
];

// Active threats feed
const THREATS = [
  { id:'t1', time: '4 min ago',  sev:'CRITICAL', type:'Lateral Movement',     msg:'api-service accessed 12 secrets outside behavioral baseline — possible compromise', cat:'Zero Trust',          src:'GCP'      },
  { id:'t2', time: '10 min ago', sev:'CRITICAL', type:'Kill Chain Detected',  msg:'Sentinel: VPN brute-force → credential success → kubectl get clusterrolebindings',  cat:'Monitoring & IR',     src:'AZURE'    },
  { id:'t3', time: '15 min ago', sev:'CRITICAL', type:'Firewall Change',      msg:'Port 22 opened to 0.0.0.0/0 on 12 VMs — no change ticket — CIS Benchmark violation', cat:'Network Security',    src:'GCP'      },
  { id:'t4', time: '22 min ago', sev:'CRITICAL', type:'Privileged Container', msg:'debug-tools-7f8b9 running privileged in prod namespace — host /proc mounted',         cat:'AKS Security',        src:'AZURE'    },
  { id:'t5', time: '40 min ago', sev:'CRITICAL', type:'PII Exposure',         msg:'142 SSNs in plaintext Cloud Logging entries since v2.3.0 deploy (9 days ago)',        cat:'Data Protection',     src:'GCP'      },
  { id:'t6', time: '1 hr ago',   sev:'WARNING',  type:'DDoS Mitigation',      msg:'2.4 Gbps UDP flood — Azure DDoS Standard mitigating 94% of traffic',                  cat:'Network Security',    src:'AZURE'    },
  { id:'t7', time: '1.5 hr ago', sev:'WARNING',  type:'MFA Fatigue Attack',   msg:'7 unexpected MFA push notifications for prod-admin in 4 minutes from Romania',        cat:'Identity & Access',   src:'AZURE'    },
  { id:'t8', time: '3 hr ago',   sev:'WARNING',  type:'Compliance Violation', msg:'PAN found in analytics.user_events — outside PCI DSS CDE scope (1,204 rows)',          cat:'Compliance',          src:'DATABASE' },
  { id:'t9', time: '4 hr ago',   sev:'WARNING',  type:'Image Vulnerability',  msg:'payments-service:2.1.4 deployed with CVE-2024-3094 (CVSS 10.0) — CI scan bypassed',    cat:'AKS Security',        src:'AZURE'    },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

const RISK_STYLES = {
  CRITICAL: { color: C.crit, bg: 'rgba(239,68,68,.15)', border: 'rgba(239,68,68,.3)' },
  HIGH:     { color: '#f97316', bg: 'rgba(249,115,22,.15)', border: 'rgba(249,115,22,.3)' },
  MEDIUM:   { color: C.warn, bg: 'rgba(245,158,11,.15)', border: 'rgba(245,158,11,.3)' },
  LOW:      { color: C.ok,   bg: 'rgba(34,197,94,.15)',  border: 'rgba(34,197,94,.3)'  },
};

function RiskBadge({ level }) {
  const s = RISK_STYLES[level] || RISK_STYLES.LOW;
  return (
    <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, letterSpacing: '0.5px' }}>
      {level}
    </span>
  );
}

function MetricDot({ status }) {
  const c = status === 'crit' ? C.crit : status === 'warn' ? C.warn : status === 'ok' ? C.ok : C.info;
  return <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0 }} />;
}

function ScoreRing({ score, color, size = 80 }) {
  const data = [{ value: score }, { value: 100 - score }];
  return (
    <div style={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
      <PieChart width={size} height={size}>
        <Pie data={data} cx={size / 2 - 1} cy={size / 2 - 1}
          innerRadius={size * 0.34} outerRadius={size * 0.46}
          startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
          <Cell fill={color} />
          <Cell fill="rgba(255,255,255,.06)" />
        </Pie>
      </PieChart>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.22, fontWeight: 800, color, lineHeight: 1, fontFamily: 'monospace' }}>{score}</span>
        <span style={{ fontSize: size * 0.1, color: 'var(--text-3)', marginTop: 1 }}>/ 100</span>
      </div>
    </div>
  );
}

function TrendSparkline({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#sg-${color.replace('#','')})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const SEV_C = { CRITICAL: C.crit, WARNING: C.warn, INFO: C.info };
const SEV_I = { CRITICAL: '🔴', WARNING: '🟡', INFO: '🔵' };

function FindingRow({ finding }) {
  const c = SEV_C[finding.sev] || '#64748b';
  return (
    <div className="flex items-start gap-2 py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
      <span style={{ fontSize: 10, marginTop: 1 }}>{SEV_I[finding.sev]}</span>
      <span className="text-xs leading-snug" style={{ color: 'var(--text-2)', flex: 1 }}>{finding.msg}</span>
    </div>
  );
}

// ─── Category card ────────────────────────────────────────────────────────────
function CategoryCard({ cat, onDrill }) {
  const Icon = cat.icon;
  const rs   = RISK_STYLES[cat.risk] || RISK_STYLES.LOW;
  const pct  = Math.round((cat.controls.passed / cat.controls.total) * 100);
  const trend = cat.trend;
  const delta = trend[trend.length - 1] - trend[0];

  return (
    <div className="rounded-xl flex flex-col overflow-hidden"
      style={{ background: 'var(--bg-secondary)', border: `1px solid var(--border)`, borderTop: `3px solid ${cat.color}` }}>
      {/* Card header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${cat.color}18`, border: `1px solid ${cat.color}30` }}>
          <Icon size={16} style={{ color: cat.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm leading-tight">{cat.name}</div>
          <div className="text-xs mt-0.5 line-clamp-1" style={{ color: 'var(--text-3)' }}>{cat.description}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <RiskBadge level={cat.risk} />
          <div className="flex items-center gap-1 text-xs" style={{ color: delta >= 0 ? C.ok : C.crit }}>
            {delta > 0 ? <TrendingUp size={10} /> : delta < 0 ? <TrendingDown size={10} /> : <Minus size={10} />}
            <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{delta > 0 ? '+' : ''}{delta}</span>
          </div>
        </div>
      </div>

      {/* Score + sparkline */}
      <div className="flex items-center gap-3 px-4 py-3">
        <ScoreRing score={cat.score} color={cat.color} size={72} />
        <div className="flex-1 min-w-0">
          <div className="text-xs mb-1 flex justify-between items-center">
            <span style={{ color: 'var(--text-3)' }}>Controls passing</span>
            <span style={{ color: cat.color, fontWeight: 700, fontFamily: 'monospace' }}>{cat.controls.passed}/{cat.controls.total}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden mb-2" style={{ background: 'var(--bg-primary)' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pct >= 80 ? C.ok : pct >= 60 ? C.warn : C.crit, borderRadius: 99 }} />
          </div>
          <div className="text-xs mb-1" style={{ color: 'var(--text-3)' }}>7-day trend</div>
          <TrendSparkline data={cat.trend} color={cat.color} />
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-px mx-3 mb-3 rounded-lg overflow-hidden" style={{ background: 'var(--border)' }}>
        {cat.metrics.map((m, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2" style={{ background: 'var(--bg-tertiary)' }}>
            <MetricDot status={m.status} />
            <div className="min-w-0 flex-1">
              <div className="text-xs truncate" style={{ color: 'var(--text-3)', fontSize: 9.5 }}>{m.label}</div>
              <div className="text-xs font-bold mono" style={{ color: m.status === 'crit' ? C.crit : m.status === 'warn' ? C.warn : m.status === 'ok' ? C.ok : 'var(--text)' }}>{m.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Top findings */}
      <div className="px-4 pb-1 flex-1">
        <div className="text-xs font-semibold mb-1" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: 9 }}>
          Open Findings ({cat.findings.length})
        </div>
        {cat.findings.slice(0, 3).map((f, i) => <FindingRow key={i} finding={f} />)}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 mt-1" style={{ borderTop: '1px solid rgba(255,255,255,.04)' }}>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>
          <span style={{ color: C.crit, fontWeight: 700 }}>{cat.controls.failed}</span> controls failing
        </span>
        <button onClick={() => onDrill(cat)}
          className="flex items-center gap-1 text-xs"
          style={{ background: 'none', border: 'none', color: cat.color, cursor: 'pointer', fontWeight: 600 }}>
          Drill down <ChevronRight size={11} />
        </button>
      </div>
    </div>
  );
}

// ─── Framework compliance card ────────────────────────────────────────────────
function FrameworkCard({ fw }) {
  const data = [
    { name: 'Pass', value: fw.passed, fill: C.ok },
    { name: 'Fail', value: fw.failed, fill: C.crit },
    { name: 'N/A',  value: fw.na,     fill: 'rgba(100,116,139,.4)' },
  ];
  const statusColor = fw.status === 'Ready' ? C.ok : fw.status === 'Needs Work' ? C.crit : C.warn;

  return (
    <div className="rounded-xl p-4 flex gap-4 items-center"
      style={{ background: 'var(--bg-secondary)', border: `1px solid var(--border)`, borderLeft: `3px solid ${fw.color}` }}>
      <div style={{ width: 72, height: 72, flexShrink: 0 }}>
        <PieChart width={72} height={72}>
          <Pie data={data} cx={35} cy={35} innerRadius={24} outerRadius={34}
            startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Pie>
        </PieChart>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold text-sm">{fw.name}</span>
          <span style={{ fontSize: 9, color: 'var(--text-3)', background: 'var(--bg-tertiary)', padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)' }}>{fw.version}</span>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}30` }}>{fw.status}</span>
        </div>
        <div className="flex items-center gap-1 mb-2">
          <div className="h-1.5 flex-1 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
            <div style={{ width: `${fw.score}%`, height: '100%', background: fw.score >= 90 ? C.ok : fw.score >= 75 ? C.warn : C.crit, borderRadius: 99 }} />
          </div>
          <span className="mono text-xs font-bold ml-2" style={{ color: fw.color }}>{fw.score}%</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span><span style={{ color: C.ok, fontWeight: 700 }}>{fw.passed}</span> <span style={{ color: 'var(--text-3)' }}>pass</span></span>
          <span><span style={{ color: C.crit, fontWeight: 700 }}>{fw.failed}</span> <span style={{ color: 'var(--text-3)' }}>fail</span></span>
          <span><span style={{ color: 'var(--text-3)' }}>{fw.na}</span> <span style={{ color: 'var(--text-3)' }}>N/A</span></span>
          <span className="ml-auto" style={{ color: 'var(--text-3)' }}>Audit: {fw.nextAudit}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Drill-down side panel ────────────────────────────────────────────────────
function DrillPanel({ cat, onClose }) {
  const Icon = cat.icon;
  return (
    <div className="fixed inset-0 z-40 flex" style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(3px)' }}
      onClick={onClose}>
      <div className="ml-auto h-full w-full overflow-y-auto flex flex-col"
        style={{ maxWidth: 520, background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border)', boxShadow: '-20px 0 60px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: `${cat.color}08` }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${cat.color}18`, border: `1px solid ${cat.color}30` }}>
            <Icon size={18} style={{ color: cat.color }} />
          </div>
          <div>
            <div className="font-bold text-sm">{cat.name}</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{cat.description}</div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}>✕</button>
        </div>

        <div className="p-5 flex flex-col gap-5">
          {/* Score + risk */}
          <div className="flex items-center gap-6 p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
            <ScoreRing score={cat.score} color={cat.color} size={88} />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base font-bold">Security Score</span>
                <RiskBadge level={cat.risk} />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-3)' }}>Controls passing</span>
                  <span style={{ color: C.ok, fontWeight: 700 }}>{cat.controls.passed} / {cat.controls.total}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-3)' }}>Controls failing</span>
                  <span style={{ color: C.crit, fontWeight: 700 }}>{cat.controls.failed}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-3)' }}>Open findings</span>
                  <span style={{ color: C.warn, fontWeight: 700 }}>{cat.findings.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 7-day trend */}
          <div className="p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color: 'var(--text-2)' }}>7-Day Score Trend</div>
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={cat.trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="dp-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={cat.color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={cat.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.04)" />
                <XAxis dataKey="d" tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis domain={[50, 100]} tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', fontSize: 11 }} />
                <Area type="monotone" dataKey="v" stroke={cat.color} strokeWidth={2} fill="url(#dp-grad)" dot={{ fill: cat.color, r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Metrics detail */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Key Metrics</div>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              {cat.metrics.map((m, i) => {
                const mc = m.status === 'crit' ? C.crit : m.status === 'warn' ? C.warn : m.status === 'ok' ? C.ok : 'var(--text)';
                return (
                  <div key={i} className="flex items-center gap-3 px-4 py-3"
                    style={{ borderBottom: i < cat.metrics.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none', background: i % 2 ? 'rgba(255,255,255,.01)' : 'transparent' }}>
                    <MetricDot status={m.status} />
                    <span className="flex-1 text-xs" style={{ color: 'var(--text-2)' }}>{m.label}</span>
                    <span className="mono text-xs font-bold" style={{ color: mc }}>{m.value}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* All findings */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>All Open Findings</div>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
              {cat.findings.map((f, i) => {
                const fc = SEV_C[f.sev] || '#64748b';
                return (
                  <div key={i} className="flex items-start gap-3 px-4 py-3"
                    style={{ borderBottom: i < cat.findings.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none' }}>
                    <span style={{ fontSize: 11, marginTop: 1 }}>{SEV_I[f.sev]}</span>
                    <div className="flex-1">
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: `${fc}18`, color: fc, marginRight: 6, border: `1px solid ${fc}30` }}>{f.sev}</span>
                      <span className="text-xs" style={{ color: 'var(--text-2)' }}>{f.msg}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Control status icon ──────────────────────────────────────────────────────
function CtrlStatus({ status }) {
  if (status === 'PASS') return <CheckCircle size={13} style={{ color: C.ok, flexShrink: 0 }} />;
  if (status === 'FAIL') return <XCircle    size={13} style={{ color: C.crit, flexShrink: 0 }} />;
  return <AlertTriangle size={13} style={{ color: C.warn, flexShrink: 0 }} />;
}

// ─── Main component ───────────────────────────────────────────────────────────
const TABS = ['Overview', 'Compliance Controls', 'Threat Feed'];

export default function SecurityDashboard() {
  const navigate   = useNavigate();
  const [tab, setTab]       = useState('Overview');
  const [drill, setDrill]   = useState(null);
  const [fwTab, setFwTab]   = useState('pci');
  const [ctrlFilter, setCtrlFilter] = useState('ALL');
  const [catFilter,  setCatFilter]  = useState('ALL');

  // overall counts
  const totalOpen  = CATEGORY_DATA.reduce((s, c) => s + c.findings.filter(f => f.sev === 'CRITICAL').length, 0);
  const totalWarn  = CATEGORY_DATA.reduce((s, c) => s + c.findings.filter(f => f.sev === 'WARNING').length, 0);
  const critCats   = CATEGORY_DATA.filter(c => c.risk === 'CRITICAL').length;
  const avgScore   = Math.round(CATEGORY_DATA.reduce((s, c) => s + c.score, 0) / CATEGORY_DATA.length);

  const fwData  = FRAMEWORKS.find(f => f.id === fwTab) || FRAMEWORKS[0];
  const controls = fwTab === 'pci' ? PCI_CONTROLS : SOC2_CONTROLS;
  const filteredCtrl = controls.filter(c => {
    if (ctrlFilter !== 'ALL' && c.status !== ctrlFilter) return false;
    if (catFilter  !== 'ALL' && c.cat   !== catFilter)   return false;
    return true;
  });
  const ctrlCats = ['ALL', ...new Set(controls.map(c => c.cat))];

  // bar chart for category scores
  const catScoreData = CATEGORY_DATA.map(c => ({ name: c.name.split(' ')[0], score: c.score, color: c.color }));

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#8b5cf6,#ef4444)', boxShadow: '0 4px 14px rgba(139,92,246,.4)' }}>
          <Shield size={17} className="text-white" />
        </div>
        <div>
          <h1 className="text-base font-bold">Security & Compliance</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Unified security posture — 7 domains · 4 frameworks · real-time threat correlation</p>
        </div>

        {/* KPI chips */}
        <div className="flex items-center gap-2 ml-4 flex-wrap">
          <KpiChip icon={ShieldAlert} label="Critical Findings" value={totalOpen} color={C.crit} />
          <KpiChip icon={AlertTriangle} label="Warnings" value={totalWarn} color={C.warn} />
          <KpiChip icon={ShieldOff} label="Critical Domains" value={critCats} color="#f97316" />
          <KpiChip icon={ShieldCheck} label="Avg Domain Score" value={`${avgScore}/100`} color={avgScore >= 75 ? C.ok : C.warn} />
        </div>

        <button onClick={() => navigate('/alerts')}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
          <ExternalLink size={11} /> Alert Center
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '9px 18px', fontSize: 12, fontWeight: tab === t ? 600 : 400,
              color: tab === t ? 'var(--text)' : 'var(--text-3)',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: tab === t ? '2px solid #8b5cf6' : '2px solid transparent' }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Overview ───────────────────────────────────────────────────────── */}
      {tab === 'Overview' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">

          {/* Overall score + frameworks row */}
          <div className="grid gap-4" style={{ gridTemplateColumns: '220px 1fr' }}>
            {/* Master score card */}
            <div className="rounded-xl p-4 flex flex-col items-center justify-center text-center"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderTop: '3px solid #8b5cf6' }}>
              <div className="text-xs font-semibold mb-3" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Security Posture Score</div>
              <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto' }}>
                <PieChart width={120} height={120}>
                  <Pie data={[{ value: OVERALL_SCORE }, { value: 100 - OVERALL_SCORE }]}
                    cx={59} cy={59} innerRadius={42} outerRadius={56}
                    startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
                    <Cell fill={OVERALL_SCORE >= 80 ? C.ok : OVERALL_SCORE >= 65 ? C.warn : C.crit} />
                    <Cell fill="rgba(255,255,255,.05)" />
                  </Pie>
                </PieChart>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 30, fontWeight: 900, color: OVERALL_SCORE >= 80 ? C.ok : OVERALL_SCORE >= 65 ? C.warn : C.crit, fontFamily: 'monospace', lineHeight: 1 }}>{OVERALL_SCORE}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-3)' }}>/ 100</span>
                </div>
              </div>
              <div className="mt-3 text-xs font-semibold" style={{ color: C.warn }}>NEEDS ATTENTION</div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>2 critical domains below 65</div>
              <div className="mt-3 w-full">
                <TrendSparkline data={SCORE_TREND.map((d, i) => ({ d: i, v: d.score }))} color="#8b5cf6" />
              </div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>7-day trend</div>
            </div>

            {/* Frameworks grid */}
            <div className="grid grid-cols-2 gap-3">
              {FRAMEWORKS.map(fw => <FrameworkCard key={fw.id} fw={fw} />)}
            </div>
          </div>

          {/* Domain score bar */}
          <div className="rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color: 'var(--text-2)' }}>Security Domain Scores</div>
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={catScoreData} barSize={28} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="rgba(255,255,255,.04)" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', fontSize: 11 }} formatter={(v) => [`${v}/100`, 'Score']} />
                <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                  {catScoreData.map((d, i) => (
                    <Cell key={i} fill={d.score >= 80 ? C.ok : d.score >= 65 ? C.warn : C.crit} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category cards grid */}
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
            {CATEGORY_DATA.map(cat => (
              <CategoryCard key={cat.id} cat={cat} onDrill={setDrill} />
            ))}
          </div>
        </div>
      )}

      {/* ── Compliance Controls ─────────────────────────────────────────────── */}
      {tab === 'Compliance Controls' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          {/* Framework selector */}
          <div className="flex gap-3 flex-wrap">
            {FRAMEWORKS.map(fw => (
              <button key={fw.id} onClick={() => { setFwTab(fw.id); setCtrlFilter('ALL'); setCatFilter('ALL'); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: fwTab === fw.id ? `${fw.color}18` : 'var(--bg-secondary)', color: fwTab === fw.id ? fw.color : 'var(--text-3)', border: `1px solid ${fwTab === fw.id ? fw.color + '40' : 'var(--border)'}`, cursor: 'pointer', transition: 'all .15s' }}>
                <FileCheck size={13} />
                {fw.name}
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: fwTab === fw.id ? `${fw.color}25` : 'var(--bg-tertiary)', color: fwTab === fw.id ? fw.color : 'var(--text-3)' }}>
                  {fw.score}%
                </span>
              </button>
            ))}
          </div>

          {/* Framework summary */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total Controls', value: fwData.passed + fwData.failed + fwData.na, color: '#64748b', icon: FileText },
              { label: 'Passing',        value: fwData.passed, color: C.ok,  icon: CheckCircle },
              { label: 'Failing',        value: fwData.failed, color: C.crit, icon: XCircle },
              { label: 'Critical Fails', value: fwData.critical, color: C.crit, icon: ShieldAlert },
            ].map(k => {
              const Icon = k.icon;
              return (
                <div key={k.label} className="rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderTop: `2px solid ${k.color}` }}>
                  <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--text-3)' }}>
                    <Icon size={11} style={{ color: k.color }} /> {k.label}
                  </div>
                  <div className="mono text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>Status:</span>
            {['ALL', 'PASS', 'FAIL', 'WARN'].map(s => (
              <button key={s} onClick={() => setCtrlFilter(s)}
                className="px-2.5 py-1 rounded text-xs font-semibold"
                style={{ background: ctrlFilter === s ? (s === 'PASS' ? 'rgba(34,197,94,.2)' : s === 'FAIL' ? 'rgba(239,68,68,.2)' : s === 'WARN' ? 'rgba(245,158,11,.2)' : 'rgba(59,130,246,.2)') : 'var(--bg-tertiary)', color: ctrlFilter === s ? (s === 'PASS' ? C.ok : s === 'FAIL' ? C.crit : s === 'WARN' ? C.warn : '#60a5fa') : 'var(--text-3)', border: `1px solid ${ctrlFilter === s ? 'currentColor' : 'var(--border)'}`, cursor: 'pointer' }}>
                {s}
              </button>
            ))}
            <span className="ml-4 text-xs" style={{ color: 'var(--text-3)' }}>Category:</span>
            <div className="flex gap-1 flex-wrap">
              {ctrlCats.map(c => (
                <button key={c} onClick={() => setCatFilter(c)}
                  className="px-2.5 py-1 rounded text-xs"
                  style={{ background: catFilter === c ? 'rgba(139,92,246,.2)' : 'var(--bg-tertiary)', color: catFilter === c ? '#a78bfa' : 'var(--text-3)', border: `1px solid ${catFilter === c ? 'rgba(139,92,246,.4)' : 'var(--border)'}`, cursor: 'pointer' }}>
                  {c}
                </button>
              ))}
            </div>
            <span className="ml-auto text-xs" style={{ color: 'var(--text-3)' }}>{filteredCtrl.length} controls</span>
          </div>

          {/* Control table */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)' }}>
                  {['Req / ID', 'Control Title', 'Category', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCtrl.map((c, i) => {
                  const statusColor = c.status === 'PASS' ? C.ok : c.status === 'FAIL' ? C.crit : C.warn;
                  const catMeta = CATEGORY_DATA.find(d => d.name === c.cat || d.name.startsWith(c.cat.split(' ')[0]));
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,.04)', background: i % 2 ? 'rgba(255,255,255,.01)' : 'transparent' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span className="mono text-xs font-bold px-2 py-0.5 rounded"
                          style={{ background: `${fwData.color}15`, color: fwData.color, border: `1px solid ${fwData.color}30` }}>
                          {c.req || c.id}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div className="flex items-center gap-2">
                          {c.critical && <span style={{ fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, background: 'rgba(239,68,68,.15)', color: C.crit, border: '1px solid rgba(239,68,68,.3)' }}>CRIT</span>}
                          <span className="text-xs" style={{ color: 'var(--text-2)' }}>{c.title}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: catMeta ? `${catMeta.color}15` : 'var(--bg-tertiary)', color: catMeta ? catMeta.color : 'var(--text-3)', border: `1px solid ${catMeta ? catMeta.color + '30' : 'var(--border)'}` }}>
                          {c.cat}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div className="flex items-center gap-1.5">
                          <CtrlStatus status={c.status} />
                          <span style={{ fontSize: 10, fontWeight: 700, color: statusColor }}>{c.status}</span>
                          {c.criteria && <span style={{ fontSize: 9, color: 'var(--text-3)', marginLeft: 4 }}>{c.criteria}</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Threat Feed ─────────────────────────────────────────────────────── */}
      {tab === 'Threat Feed' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Active Threats',  value: THREATS.filter(t => t.sev === 'CRITICAL').length, color: C.crit, icon: ShieldAlert },
              { label: 'Total Alerts',    value: THREATS.length,  color: '#64748b',  icon: Bell },
              { label: 'Domains Affected',value: new Set(THREATS.map(t => t.cat)).size, color: C.warn, icon: Globe },
              { label: 'Sources Affected',value: new Set(THREATS.map(t => t.src)).size, color: C.aks,  icon: Server },
            ].map(k => {
              const Icon = k.icon || Shield;
              return (
                <div key={k.label} className="rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderTop: `2px solid ${k.color}` }}>
                  <div className="flex items-center gap-1.5 text-xs mb-2" style={{ color: 'var(--text-3)' }}>
                    <Icon size={11} style={{ color: k.color }} /> {k.label}
                  </div>
                  <div className="mono text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
                </div>
              );
            })}
          </div>

          {/* Threat timeline */}
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
              <div className="flex items-center gap-2">
                <Activity size={13} style={{ color: C.crit }} />
                <span className="text-xs font-semibold">Active Threat Feed</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs" style={{ color: C.ok }}>
                <span className="pulse-dot" /> LIVE
              </div>
            </div>
            {THREATS.map((t, i) => {
              const catMeta = CATEGORY_DATA.find(c => c.name === t.cat);
              const CatIcon = catMeta?.icon || Shield;
              const sc = SEV_C[t.sev] || '#64748b';
              return (
                <div key={t.id} className="flex items-start gap-3 px-4 py-4 hover:bg-white/[0.02] transition-colors"
                  style={{ borderBottom: i < THREATS.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: sc }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 3, background: `${sc}18`, color: sc, border: `1px solid ${sc}30` }}>{t.sev}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 3, background: catMeta ? `${catMeta.color}18` : 'var(--bg-tertiary)', color: catMeta ? catMeta.color : 'var(--text-3)', border: `1px solid ${catMeta ? catMeta.color + '30' : 'var(--border)'}`, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <CatIcon size={8} /> {t.cat}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>{t.type}</span>
                      <span className="ml-auto mono text-xs" style={{ color: 'var(--text-3)' }}>{t.time}</span>
                    </div>
                    <p className="text-xs leading-snug" style={{ color: 'var(--text-2)' }}>{t.msg}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: `${SRC_C[t.src] || '#64748b'}15`, color: SRC_C[t.src] || '#64748b', border: `1px solid ${SRC_C[t.src] || '#64748b'}30` }}>{t.src}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Category distribution */}
          <div className="rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color: 'var(--text-2)' }}>Threats by Security Domain</div>
            <div className="grid gap-2">
              {CATEGORY_DATA.map(cat => {
                const critCount = cat.findings.filter(f => f.sev === 'CRITICAL').length;
                const warnCount = cat.findings.filter(f => f.sev === 'WARNING').length;
                const total = cat.findings.length;
                return (
                  <div key={cat.id} className="flex items-center gap-3">
                    <cat.icon size={12} style={{ color: cat.color, flexShrink: 0 }} />
                    <span className="text-xs w-36 shrink-0" style={{ color: 'var(--text-2)' }}>{cat.name}</span>
                    <div className="flex-1 h-4 rounded-full overflow-hidden relative" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
                      <div style={{ width: `${(critCount / 5) * 100}%`, height: '100%', background: C.crit, borderRadius: 99, float: 'left' }} />
                      <div style={{ width: `${(warnCount / 5) * 100}%`, height: '100%', background: C.warn, borderRadius: 0, float: 'left' }} />
                    </div>
                    <div className="flex items-center gap-2 text-xs shrink-0">
                      {critCount > 0 && <span style={{ color: C.crit, fontWeight: 700 }}>{critCount}c</span>}
                      {warnCount > 0 && <span style={{ color: C.warn, fontWeight: 700 }}>{warnCount}w</span>}
                      <span style={{ color: 'var(--text-3)' }}>{total} total</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Drill-down panel */}
      {drill && <DrillPanel cat={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

function KpiChip({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
      style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
      <Icon size={12} style={{ color }} />
      <span className="text-xs" style={{ color: 'var(--text-3)' }}>{label}</span>
      <span className="text-xs font-bold mono" style={{ color }}>{value}</span>
    </div>
  );
}
